/**
 * 背景ツアー「福岡めぐり」の純粋ロジック。
 *
 * Phaser 非依存。時間→景色インデックスの計算、正規化座標の変換、
 * ホットスポットの当たり判定を担当する。
 */

import type { TourHotspot, TourScene } from '../config/types';

export interface TourState {
  /** 現在表示中の景色インデックス（0-based）。 */
  index: number;
  /** 現在の景色が始まってからの経過ミリ秒。 */
  elapsedInSceneMs: number;
  /** 次の景色への切り替え進捗（0〜1）。 */
  progressToNext: number;
  /** このフレームで景色が切り替わったか。 */
  justChanged: boolean;
}

/**
 * 経過時間から現在の景色インデックスを求める。
 *
 * loop=false の場合、最後の景色を超えても最後に留まる。
 */
export function sceneIndexAt(
  elapsedMs: number,
  secondsPerScene: number,
  sceneCount: number,
  loop: boolean,
): number {
  if (sceneCount <= 0) return 0;
  const sceneDurationMs = Math.max(1, secondsPerScene * 1000);
  const rawIndex = Math.floor(elapsedMs / sceneDurationMs);
  if (loop) return ((rawIndex % sceneCount) + sceneCount) % sceneCount;
  return Math.min(rawIndex, sceneCount - 1);
}

/**
 * 経過時間からツアー状態を計算する。
 */
export function computeTourState(
  elapsedMs: number,
  secondsPerScene: number,
  sceneCount: number,
  loop: boolean,
  previousIndex: number,
): TourState {
  const sceneDurationMs = Math.max(1, secondsPerScene * 1000);
  const index = sceneIndexAt(elapsedMs, secondsPerScene, sceneCount, loop);
  // ループ時も正しく 0〜sceneDurationMs の範囲に収めるため、
  // ラップ後のインデックスではなく通し番号（elapsedMs % sceneDurationMs）を使う。
  const elapsedInSceneMs = elapsedMs % sceneDurationMs;
  const progressToNext =
    loop || !isLastScene(index, sceneCount)
      ? Math.min(1, elapsedInSceneMs / sceneDurationMs)
      : 1;

  return {
    index,
    elapsedInSceneMs,
    progressToNext,
    justChanged: index !== previousIndex,
  };
}

/** 指定インデックスが最後の景色かどうか。 */
export function isLastScene(index: number, sceneCount: number): boolean {
  return index >= sceneCount - 1;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * 正規化座標（0〜1）を論理座標（480x720）へ変換する。
 *
 * 背景画像は画面全体（world.width x world.height）にフィットさせて表示する。
 */
export function tourNormalizedToWorld(
  nx: number,
  ny: number,
  worldWidth: number,
  worldHeight: number,
): { x: number; y: number } {
  return {
    x: clamp01(nx) * worldWidth,
    y: clamp01(ny) * worldHeight,
  };
}

/** ホットスポットの当たり判定。 */
export function hitTestHotspot(
  pointerX: number,
  pointerY: number,
  hotspotX: number,
  hotspotY: number,
  radius: number,
): boolean {
  const dx = pointerX - hotspotX;
  const dy = pointerY - hotspotY;
  return dx * dx + dy * dy <= radius * radius;
}

/**
 * マーカーを実際に置く位置。**描画も当たり判定も必ずこれを通すこと。**
 *
 * 器の縁より下に置かれたマーカーは、器の内側（半透明）ごしに光って見えるのに
 * 押しても何も起きない、という状態になる。かつては当たり判定だけが縁で切られ、
 * 描画は切られていなかったため、**1301個中259個（25県）がその状態だった。**
 * 描画と判定で別々に座標を作ったことが原因なので、ここに一本化してある。
 *
 * 縁より下に来るものは縁の少し上へ寄せる。景色のどこを指しているかは多少ずれるが、
 * 「光っているのに押せない」よりはよい。**データ側で y を 0.55 以下にするのが本来の直し方。**
 */
export function tourHotspotWorldPos(
  nx: number,
  ny: number,
  worldWidth: number,
  worldHeight: number,
  rimY: number,
  rimMargin: number,
): { x: number; y: number } {
  const world = tourNormalizedToWorld(nx, ny, worldWidth, worldHeight);
  return { x: world.x, y: Math.min(world.y, rimY - rimMargin) };
}

/** 現在の景色内で、指定座標にヒットするホットスポットを返す。 */
export function findHitHotspot(
  pointerX: number,
  pointerY: number,
  scene: TourScene,
  worldWidth: number,
  worldHeight: number,
  rimY: number,
  hitRadius: number,
  rimMargin: number,
): TourHotspot | null {
  for (const hotspot of scene.hotspots) {
    const world = tourHotspotWorldPos(hotspot.x, hotspot.y, worldWidth, worldHeight, rimY, rimMargin);
    if (hitTestHotspot(pointerX, pointerY, world.x, world.y, hitRadius)) {
      return hotspot;
    }
  }
  return null;
}

/** Google マップの検索 URL を生成する。 */
export function googleMapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
